import {
  Table,
  Tbody,
  Tr,
  Td,
  Skeleton,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
} from '@tanstack/react-table';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidV4 } from 'uuid';

import {
  utils as commonUtils,
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';

import { LoanActivity } from './activity';
import { useEditLoanContext } from './useEditLoanContext';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  TableBETA,
  TableHeaderBETA,
} from '~/packages/common-frontend/tables/TableBETA';
import { formatCurrencyAmount } from '~/packages/common-frontend/utils';

type LoansTableProps = { loanId: string };

const LoanActivityStatus = ({ status }: { status: LoanActivity['status'] }) => {
  switch (status) {
    case 'approved':
      return (
        <Typography TextProps={{ color: 'green.400' }}>
          <Icon icon="circle-check" /> {commonUtils.toTitleCase(status)}
        </Typography>
      );
    case 'rejected':
      return (
        <Typography TextProps={{ color: 'green.400' }}>
          <Icon icon="circle-check" /> {commonUtils.toTitleCase(status)}
        </Typography>
      );
    case 'submitted':
      return (
        <Typography TextProps={{ color: 'blue.400' }}>
          <Icon icon="circle-info" /> {commonUtils.toTitleCase(status)}
        </Typography>
      );
    case 'pending':
      return (
        <Typography TextProps={{ color: 'blue.400' }}>
          <Icon icon="circle-info" /> {commonUtils.toTitleCase(status)}
        </Typography>
      );
    case 'canceled':
      return (
        <Typography TextProps={{ color: 'red.400' }}>
          <Icon icon="circle-x" /> {commonUtils.toTitleCase(status)}
        </Typography>
      );
    case 'unknown':
      return (
        <Typography TextProps={{ color: 'gray.400' }}>
          <Icon icon="circle-question-mark" /> {commonUtils.toTitleCase(status)}
        </Typography>
      );
    case 'paid':
      return (
        <Typography TextProps={{ color: 'green.400' }}>
          <Icon icon="circle-check" /> {commonUtils.toTitleCase(status)}
        </Typography>
      );
    default:
      return finleyAssertUnreachable(status);
  }
};

const COLUMN_DEFS: ColumnDef<LoanActivity, any>[] = [
  {
    id: 'edited',
    header: () => null,
    accessorKey: 'isEdited',
    cell: function IsEditedCell({ getValue }) {
      return getValue() ? <Typography>Edited</Typography> : null;
    },
    enableSorting: false,
  },
  {
    id: 'effectiveDate',
    header: 'Effective Date',
    accessorKey: 'effectiveDate',
    cell: function EffectiveDateCell({ getValue }) {
      const effectiveDate = getValue();
      return effectiveDate ? (
        <Typography>
          {commonDate.fullDateStrToHumanDateString(effectiveDate)}
        </Typography>
      ) : (
        <Typography>-</Typography>
      );
    },
  },
  {
    id: 'type',
    header: 'Type',
    accessorKey: 'type',
    cell: function TypeCell({ getValue }) {
      return <Typography>{commonUtils.toTitleCase(getValue())}</Typography>;
    },
  },
  {
    id: 'amount',
    accessorKey: 'amount',
    cell: function AmountCell({ getValue }) {
      return (
        <Typography>
          {formatCurrencyAmount(getValue(), Bank.Currency.USD)}
        </Typography>
      );
    },
  },
  {
    id: 'activityId',
    header: 'Activity ID',
    accessorKey: 'activityId',
    cell: function ActivityIdCell({ getValue }) {
      const value = getValue();
      if (!value || typeof value !== 'string') {
        return <Typography>-</Typography>;
      }
      return <Typography>...{value.substring(value.length - 5)}</Typography>;
    },
  },
  {
    id: 'facility',
    header: 'Facility',
    accessorKey: 'facility',
    cell: function FacilityCell({ getValue }) {
      const { creditFacilityName, externalFacilityId } = getValue();
      return (
        <Typography>
          {`${creditFacilityName}${
            externalFacilityId ? `(${externalFacilityId})` : ''
          }`}
        </Typography>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    cell: function StatusCell({ getValue }) {
      // @jendevelops TODO format status cell
      return <LoanActivityStatus status={getValue()} />;
    },
  },
];

export const LoansActivityTable = ({ loanId }: LoansTableProps) => {
  const navigate = useNavigate();
  const context = useEditLoanContext();

  const table = useReactTable({
    // @jendevelops TODO format loans table
    data: context.status === 'ready' ? context.state.activity : [],
    columns: COLUMN_DEFS,
    getCoreRowModel: getCoreRowModel(),
  });

  if (context.status !== 'ready') {
    return (
      <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <TableHeaderBETA table={table} />
        <Tbody>
          {Array.from(Array(5).keys()).map(() => (
            <Tr key={uuidV4()}>
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <Td key={header.id}>
                    <Skeleton w="100%" h="2rem" />
                  </Td>
                )),
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  }

  return (
    <>
      <TableBETA
        table={table}
        onRowClick={(row) => {
          if (row.activityId)
            navigate(`/loans/${loanId}/edit/fundingRequests/${row.activityId}`);
        }}
      />
      {context.state.activity.length === 0 && (
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>No loan activity found</AlertDescription>
        </Alert>
      )}
    </>
  );
};
